<template>
  <div class="relations-table table-component">

    <artisio-table :pagination="tableData.pagination" selectable :sort="tableData.ordering"
                   :items="tableData.rows"
                   :busy="tableData.loading" :fields="tableFields" :index="false" :checkboxes="true"
                   :search="tableData.search" :enable-simple-search="false" :enable-export="false"
                   @paramsChanged="getTableData" show-toggle-columns :extra-data="extraData"
                   @update-toggle-columns="updateToggleColumns" @on-column-save="updateToggleColumns"
                   filter-toggle-string="relations-table">

      <template v-slot:search-form-placeholder>
        <search-collapse table-id="relations-table">
          <relations-search :exclude-search-fields="excludeSearchFields" :sale="tableData ? tableData.sale : null"/>
        </search-collapse>
      </template>

      <template v-slot:header-buttons="{selectedItems}">
        <add-new-button v-permission="'change_customer'" @click="onNewRelationClick">
          <font-awesome-icon icon="plus-circle" class="icon"/>
          {{ $t('New Relation') }}
        </add-new-button>
      </template>

      <template slot="cell(related_customer_uuid)" slot-scope="{item: relation}">
        <span v-if="relation.related_customer">
          <router-link v-if="$hasPermission('view_customer')"
                       :to="{name: 'customers.dashboard', params: { id: relation.related_customer.uuid }}">
            {{ relation.related_customer.display_name }}
          </router-link>
          <span v-else>
            {{ relation.related_customer.display_name }}
          </span>
          - {{ relation.related_customer.email }}
        </span>
      </template>
      <template slot="cell(created_at)" slot-scope="{item: relation}">
        {{ relation.created_at | toDate }} by {{ relation.created_by.display_name }}
      </template>
      <template slot="cell(updated_at)" slot-scope="{item: relation}">
        {{ relation.updated_at | toDate }} by {{ relation.updated_by.display_name }}
      </template>
      <template slot="cell(intermediary_commission_rate)" slot-scope="{item: relation}">
        <span v-if="relation.type === 'intermediary'">
          {{ relation.intermediary_commission_rate | asPercentage }}
        </span>
      </template>
      <template slot="cell(intermediary_flat_price)" slot-scope="{item: relation}">
        <span v-if="relation.type === 'intermediary'">
          {{ relation.intermediary_flat_price }}
        </span>
      </template>
      <template slot="cell(intermediary_commission_from)" slot-scope="{item: relation}">
        <div v-if="relation.intermediary_commission_from && relation.type === 'intermediary'">
          <span v-for="from in relation.intermediary_commission_from"
                :key="`row-from-${from}-${relation.uuid}`" class="badge badge-info mr-2">
            {{ from | removeUnderscores | toUpperCaseFirst }}
          </span>
        </div>
      </template>
      <template slot="cell(including_vat)" slot-scope="{item: relation}">
        <template v-if="relation.type === 'intermediary'">
          <span v-if="relation.including_vat" class="badge badge-success">{{ $t("Yes") }}</span>
          <span v-else class="badge badge-danger">{{ $t("No") }}</span>
        </template>
      </template>
      <template slot="cell(type)" slot-scope="{item: relation}">
        {{ relation.type | removeUnderscores | toUpperCaseFirst | translate }}
        <action-buttons v-if="!hideActionButtons"
                        :view="false" @editClick="onEditClick(relation)" @deleteClick="onRemoveClick(relation)"/>
      </template>
      <template slot="cell(hidden)" slot-scope="{item: relation}">
        <template v-if="relation.type === 'intermediary'">
          <span v-if="relation.hide_from_communication" class="badge badge-success">{{ $t("Yes") }}</span>
          <span v-else class="badge badge-danger">{{ $t("No") }}</span>
        </template>
      </template>
      <template slot="cell(related_to_customer_uuid)" slot-scope="{item: relation}">
        <span v-if="relation.related_object && relation.related_object.customer_no">
          <router-link v-if="$hasPermission('view_customer')"
                       :to="{ name: 'customers.dashboard', params: { id: relation.related_object.uuid }}">
            {{ relation.related_object.display_name }}
          </router-link>
          <span v-else>
            {{ relation.related_object.display_name }}
          </span>
           - {{ relation.related_object.email }}
        </span>

      </template>
      <template slot="cell(related_to_receipt_uuid)" slot-scope="{item: relation}">
        <router-link v-if="relation.related_object.receipt_no"
                     :to="{ name: 'receipt.dashboard', params: { id: relation.related_object.uuid }}">
          {{ relation.related_object.receipt_no }}
        </router-link>
      </template>
    </artisio-table>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import {
    RELATIONS_TABLE_NAME, SOURCE_RELATIONS_TABLE
  } from "../constants";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import cloneDeep from "lodash/cloneDeep";
  import RelationsSearch from "@/modules/relations/RelationsSearch";
  import AddNewButton from "@/core/components/common/AddNewButton";
  import ActionButtons from "@/core/components/ams-table/ActionButtons";

  const {mapActions, mapState} = createNamespacedHelpers('relations');

  export default {
    name: "RelationsTable",
    components: {
      ActionButtons,
      AddNewButton,
      RelationsSearch,
      SearchCollapse,
      ArtisioTable,
    },
    props: {
      hideActions: {
        type: Array,
        default: () => []
      },
      excludeColumns: {
        type: Array,
        default: () => []
      },
      excludeSearchFields: {
        type: Array,
        default: () => []
      },
      hideActionButtons: {
        type: Boolean,
        default: () => false
      },
    },
    data: () => {
      return {}
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
        currentObject: state => state.tableData[state.tableSource].object,
        tableSource: state => state.tableSource
      }),
      tableFields() {
        return cloneDeep(this.toggleColumns.fields)
          .filter(field => !this.computedExcludeColumns.includes(field.key) && field.isVisible)
      },

      computedExcludeColumns() {
        const excludeColumns = cloneDeep(this.excludeColumns);
        if (!this.enableInventoryQuantity) {
          excludeColumns.push('quantity');
        }
        return excludeColumns;
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: this.computedExcludeColumns,
          tableName: `${RELATIONS_TABLE_NAME}.${this.tableSource}`,
        }
      },
    },
    methods: {
      ...mapActions([
        'getTableData',
        'updateToggleColumns',
        'showModal',
        'removeRelation',
      ]),
      hideAction(actionName) {
        return this.hideActions.includes(actionName)
      },
      onNewRelationClick() {
        this.showModal({
          source: SOURCE_RELATIONS_TABLE,
          sourceObject: this.currentObject
        })
      },
      onEditClick(relation) {
        this.showModal({
          source: SOURCE_RELATIONS_TABLE,
          object: relation,
          sourceObject: this.currentObject
        })
      },
      onRemoveClick(relation) {
        this.$confirm(
          this.$i18n.t("Are you sure you want to delete following relation?"),
          this.$i18n.t('Relation was successfully deleted'),
          this.removeRelation,
          relation
        );
      },
    },
    mounted() {
      this.getTableData();
    },
    destroyed() {
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .artisio-table {
  }

</style>

